import { useState, useRef, ChangeEvent } from 'react'
import { router } from '@inertiajs/react'
import { ArrowLeft, Send, Paperclip, FileIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import { useOrgPath } from '@/lib/pathFor'
import { toast } from 'sonner'

interface ComposeProps {
  from_address: string
}

interface PendingFile {
  id: string
  file: File
  name: string
  size: number
}

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Compose({ from_address }: ComposeProps) {
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pathFor = useOrgPath()

  function validate(): boolean {
    if (!to.trim()) {
      toast.error('Recipient (To) is required')
      return false
    }
    if (!subject.trim()) {
      toast.error('Subject is required')
      return false
    }
    if (!body.trim()) {
      toast.error('Email body cannot be empty')
      return false
    }
    return true
  }

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large (max 25MB)')
      return
    }

    setPendingFiles(prev => [...prev, {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
    }])

    e.target.value = ''
  }

  function removeFile(id: string) {
    setPendingFiles(prev => prev.filter(f => f.id !== id))
  }

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }

  async function uploadPendingFiles(emailId: string): Promise<string[]> {
    const errors: string[] = []
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      try {
        const resp = await fetch(pathFor(`/emails/${emailId}/attachments`), {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRF-Token': csrfToken() },
        })
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}))
          errors.push(`${pf.name}: ${data.error || 'upload failed'}`)
        }
      } catch {
        errors.push(`${pf.name}: network error`)
      }
    }
    return errors
  }

  async function createEmail(sendImmediately: boolean) {
    if (!validate()) return

    setSaving(true)
    try {
      // Always create as draft first (so we can upload attachments before sending)
      const resp = await fetch(pathFor('/emails'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ to, subject, body }),
      })

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        toast.error(data.error || 'Failed to create email')
        setSaving(false)
        return
      }

      const { email_thread_id, id: emailId } = await resp.json()

      // Upload pending files while email is still a draft
      if (pendingFiles.length > 0) {
        const uploadErrors = await uploadPendingFiles(emailId)
        if (uploadErrors.length > 0) {
          toast.error(`Some attachments failed: ${uploadErrors.join('; ')}`)
        }
      }

      // Then send if requested
      if (sendImmediately) {
        const sendResp = await fetch(pathFor(`/emails/${emailId}/send`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken(),
          },
        })
        if (!sendResp.ok) {
          const data = await sendResp.json().catch(() => ({}))
          toast.error(data.error || 'Draft created but failed to send — you can retry from the thread')
        }
      }

      router.visit(pathFor(`/inbox/threads/${email_thread_id}`))
    } catch {
      toast.error('Failed to create email')
      setSaving(false)
    }
  }

  function handleSaveDraft() {
    createEmail(false)
  }

  function handleSendEmail() {
    createEmail(true)
  }

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="flex h-14 items-center gap-3 border-b px-6">
        <button
          onClick={() => router.visit(pathFor('/inbox'))}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="text-sm font-semibold text-foreground">New Email</h2>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* From (read-only) */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input
              value={from_address}
              disabled
              className="h-9 text-[13px] bg-muted/50"
            />
          </div>

          {/* To */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              className="h-9 text-[13px]"
              autoFocus
            />
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="h-9 text-[13px]"
            />
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Body</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your email..."
              className="min-h-[300px] text-[13px] leading-[1.6]"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {pendingFiles.map((pf) => (
                  <div
                    key={pf.id}
                    className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-xs"
                  >
                    <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="max-w-[150px] truncate">{pf.name}</span>
                    <span className="text-muted-foreground">({formatFileSize(pf.size)})</span>
                    <button
                      onClick={() => removeFile(pf.id)}
                      className="ml-1 text-muted-foreground hover:text-red-600 cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-3 w-3 mr-1.5" />
                Attach File
              </Button>
              <span className="text-[10px] text-muted-foreground">Max 25MB per file</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center pt-2">
            <button
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={() => router.visit(pathFor('/inbox'))}
            >
              Cancel
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" className="h-8 text-xs cursor-pointer" onClick={handleSaveDraft} disabled={saving}>
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-black hover:bg-gray-800 text-white cursor-pointer"
                onClick={handleSendEmail}
                disabled={saving}
              >
                <Send className="size-3" />
                Send Email
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Compose.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
